import React from 'react'
import { Answer as AnswerType } from '@/types/admin'
import PieChart from '@/components/Admin/Charts/PieChart'
import WordCloudComponent from '@/components/Admin/Charts/WordCloud'

interface SummaryTabProps {
  answers: AnswerType[]
  survey: {
    id: number
    name: string
    service_name?: string
  }
  summaryData: Record<string, QuestionSummary>
  metrics: {
    csat_score: number
    nps_score: number
    accessibility_csat_score: number
  }
}

export interface QuestionSummary {
  question_body: string
  kind: string
  is_csat?: boolean
  answers?: Record<string, number>
  word_cloud_data?: Array<{ text: string; value: number }>
  total_responses: number
}

const SummaryTab: React.FC<SummaryTabProps> = ({ survey, summaryData, metrics }) => {
  const { csat_score: csatScore, nps_score: npsScore, accessibility_csat_score: accessibilityScore } = metrics

  const colorPalette = [
    '#3b82f6', // blue-500
    '#16a34a', // green-600
    '#ef4444', // red-500
    '#8b5cf6', // violet-500
    '#f97316', // orange-500
    '#14b8a6', // teal-500
    '#eab308', // yellow-500
    '#ec4899', // pink-500
    '#6b7280', // gray-500
    '#0891b2' // cyan-600
  ]

  const formatChartData = (questionData: QuestionSummary) => {
    if (!questionData.answers) return []

    return Object.entries(questionData.answers).map(([name, value], index) => ({
      name,
      value,
      color: colorPalette[index % colorPalette.length]
    }))
  }

  const renderChart = (questionData: QuestionSummary) => {
    if (questionData.total_responses === 0) {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-500">No hay respuestas para esta pregunta aún.</p>
        </div>
      )
    }

    switch (questionData.kind) {
      case 'nps':
      case 'multiple_option':
        return (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <PieChart data={formatChartData(questionData)} totalResponses={questionData.total_responses} />
          </div>
        )

      case 'open_ended':
        return (
          <WordCloudComponent data={questionData.word_cloud_data || []} totalResponses={questionData.total_responses} />
        )

      default:
        return (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-gray-500">Tipo de pregunta no soportado para gráficos.</p>
          </div>
        )
    }
  }

  return (
    <div className="bg-white rounded-r-lg">
      <div className="mx-4">
        {/* Resumen de indicadores principales */}
        <div className="mb-8 pt-6">
          <h2 className="text-lg font-semibold mb-6">Resumen de indicadores principales</h2>
          <div className={`grid grid-cols-1 gap-6 ${
            survey.name === 'accessibility' ? 'md:grid-cols-3' : 'md:grid-cols-2'
          }`}>
            {/* CSAT - Siempre se muestra */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">📊</div>
                <div>
                  <h3 className="font-semibold">Satisfacción de experiencia general (CSAT)</h3>
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-800">{csatScore}%</div>
            </div>

            {/* NPS - Siempre se muestra */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">📈</div>
                <div>
                  <h3 className="font-semibold">Puntuación Neta de Promotores (NPS)</h3>
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-800">{npsScore}%</div>
            </div>

            {/* Accesibilidad - Solo se muestra para encuestas de accesibilidad */}
            {survey.name === 'accessibility' && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">♿</div>
                  <div>
                    <h3 className="font-semibold">Satisfacción de accesibilidad (CSAT)</h3>
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-800">{accessibilityScore}%</div>
              </div>
            )}
          </div>
        </div>
        {/* Parte I: Preguntas de satisfacción general CSAT */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          {/* Gráficos de preguntas */}
          <div className="space-y-8">
            {Object.entries(summaryData).map(([questionId, questionData], index) => (
              <div key={questionId} className="border-b border-gray-200 pb-6 last:border-b-0">
                <h3 className="font-medium mb-6">
                  {index + 1}. {questionData.question_body}
                </h3>

                {renderChart(questionData)}
              </div>
            ))}

            {Object.keys(summaryData).length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">No hay datos de gráficos disponibles aún.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SummaryTab
